import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Plane, Send, CheckCircle2, Clock } from "lucide-react";
import { BRAND_ORANGE, PRACTICE } from "../lib/brand";

const SCOPE_OPTIONS = [
  "Post-op complications",
  "Implant emergencies",
  "Extraction complications",
  "Acute facial pain",
  "Acute infections / abscess",
  "Trauma triage",
];

export default function Coverage() {
  const [form, setForm] = useState({
    away_doctor_name: "", practice: "", practice_phone: "",
    start_date: "", end_date: "", contact_email: "",
    forwarding_phone: "", typical_call_volume: "", notes: "",
  });
  const [scope, setScope] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggle = (v) => setScope(s => s.includes(v) ? s.filter(x => x !== v) : [...s, v]);

  const load = () => api.get("/coverage/requests").then(r => setHistory(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/coverage/requests", { ...form, coverage_scope: scope });
      toast.success("Coverage request emailed to the practice");
      setForm({ away_doctor_name: "", practice: "", practice_phone: "", start_date: "", end_date: "", contact_email: "", forwarding_phone: "", typical_call_volume: "", notes: "" });
      setScope([]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="px-8 py-10 lg:px-14 lg:py-14 max-w-5xl">
      <div className="uppercase-label">Emergency Coverage</div>
      <h1 className="font-heading text-4xl font-semibold mt-2">Request coverage while you're away</h1>
      <p className="text-muted-foreground mt-3 max-w-2xl">
        Submit your dates and we'll cover your patients for post-op questions, implant emergencies, and acute issues
        while you're on vacation or out of office.
      </p>

      <div className="grid lg:grid-cols-[1fr_360px] gap-8 mt-10">
        <form onSubmit={submit} className="module-card p-8 bg-white space-y-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-md text-white flex items-center justify-center" style={{ backgroundColor: BRAND_ORANGE }}>
              <Plane size={20} />
            </div>
            <div>
              <div className="font-heading text-xl font-semibold">New coverage request</div>
              <div className="text-xs text-muted-foreground">Submitted to {PRACTICE}</div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <In label="Doctor going away *" testid="cov-doctor" required value={form.away_doctor_name} onChange={(v) => update("away_doctor_name", v)} />
            <In label="Practice name *" testid="cov-practice" required value={form.practice} onChange={(v) => update("practice", v)} />
            <In label="Practice phone *" testid="cov-practice-phone" required value={form.practice_phone} onChange={(v) => update("practice_phone", v)} />
            <In label="Contact email *" testid="cov-email" required type="email" value={form.contact_email} onChange={(v) => update("contact_email", v)} />
            <In label="Coverage start *" testid="cov-start" required type="date" value={form.start_date} onChange={(v) => update("start_date", v)} />
            <In label="Coverage end *" testid="cov-end" required type="date" value={form.end_date} onChange={(v) => update("end_date", v)} />
            <In label="Forwarding phone (optional)" testid="cov-forward" value={form.forwarding_phone} onChange={(v) => update("forwarding_phone", v)} />
            <In label="Typical call volume" testid="cov-volume" value={form.typical_call_volume} onChange={(v) => update("typical_call_volume", v)} placeholder="e.g., 1–2 calls/week" />
          </div>

          <div>
            <div className="text-sm font-semibold mb-2">Coverage scope</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {SCOPE_OPTIONS.map((s) => (
                <label key={s} className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm cursor-pointer ${scope.includes(s) ? "border-[#F26A24] bg-[#FDE6D8]" : "border-gray-200 hover:bg-gray-50"}`}>
                  <input type="checkbox" data-testid={`cov-scope-${s.toLowerCase().replace(/[^a-z]/g,'-')}`} checked={scope.includes(s)} onChange={() => toggle(s)} className="accent-[#F26A24]" />
                  {s}
                </label>
              ))}
            </div>
          </div>

          <label className="block">
            <span className="text-xs uppercase tracking-wider font-medium text-gray-600">Notes / special instructions</span>
            <textarea data-testid="cov-notes" rows={3} value={form.notes} onChange={(e) => update("notes", e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-gray-200 text-sm" />
          </label>

          <button data-testid="cov-submit" type="submit" disabled={submitting} className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-white font-medium disabled:opacity-60" style={{ backgroundColor: BRAND_ORANGE }}>
            <Send size={16} /> {submitting ? "Sending..." : "Submit coverage request"}
          </button>
        </form>

        <aside>
          <div className="uppercase-label">Your requests</div>
          <div className="mt-3 space-y-3">
            {history.length === 0 && <div className="text-sm text-muted-foreground">No requests yet.</div>}
            {history.map((r) => (
              <div key={r.id} className="module-card p-4 bg-white">
                <div className="flex items-center gap-2">
                  {r.status === "pending" ? <Clock size={14} className="text-amber-600" /> : <CheckCircle2 size={14} className="text-emerald-600" />}
                  <span className="text-xs uppercase tracking-wider font-medium">{r.status}</span>
                </div>
                <div className="font-medium mt-2">{r.away_doctor_name}</div>
                <div className="text-xs text-muted-foreground">{r.start_date} → {r.end_date}</div>
                <div className="text-xs text-muted-foreground mt-1">{r.practice}</div>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}

function In({ label, testid, type = "text", required, value, onChange, placeholder }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider font-medium text-gray-600">{label}</span>
      <input data-testid={testid} type={type} required={required} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-md border border-gray-200 text-sm focus:outline-none focus:border-[#F26A24]" />
    </label>
  );
}
